import {
  selectCitiesCache,
  selectFinalRound,
  selectGameType,
  selectPlayers,
  selectRound,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {getBotBonusProfile} from "../getBotBonusProfile";
import {getRandomElement} from "../../other/getRandomElement";
import {BOT_ARCHETYPES} from "./archetypes";
import {getFactionPreferences} from "./factionPreferences";
import {checkIfW42CelectialsStormPlayer} from "../factions/celectialsBot";
import {getBotStrategy} from "./botStrategyStore";
import {calcPlayerArmyStrength, calcStrongestOpponentArmy, findWeakEnemyCities} from "../evaluation/armyStrength";
import {getBotGridRenderer} from "../botGrid";
import {FINAL_ROUND} from "../../../configs/game";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {getRoundLimit} from "../../checkIfFinalRound";
import {isPreW35DoctrinePlayer} from "../abTestGates";
import {getHexDistance} from "../../coords/getHexDistance";
import {
  GARRISON_DOCTRINES,
  getFactionGarrisonDoctrine,
  W35_DOCTRINE_LOCAL_FORCE_DISTANCE,
  W35_DOCTRINE_REAR_EXPOSURE_MODE,
  W35_DOCTRINE_RUSH_LOCAL_FORCE_RATIO,
  W35_DOCTRINE_RUSH_MIN_LOCAL_FORCE,
} from "../../../configs/botGarrison";
import {selectGarrisonAssignments} from "../evaluation/garrisons";

// С какого раунда экономические архетипы переходят к наращенной агрессии
const ECONOMIC_AGGRESSION_ROUND = 9;
// Превосходство противника, при котором бот уходит в оборону
const OVERWHELMED_RATIO = 2;
// С какого раунда и при каком перевесе проекции очков бот защищает лидерство
const LEAD_PROTECTION_ROUND = 6;
const LEAD_PROTECTION_RATIO = 1.25;

/**
 * Проекция финального счета игрока: текущие очки + доход очков за оставшиеся
 * раунды + конвертация монет (текущих и будущего дохода) в очки в конце игры.
 * Доходы coinsIncome/winPointsIncome поддерживаются на игроках редьюсером доходов.
 */
const projectFinalScore = (player, remainingRounds) => {
  const conversionRate = isPlayerFaction(player, FACTIONS.ENLIGHTENED) ? 1 : 0.5;
  const projectedCoins = (player.coins || 0) + remainingRounds * (player.coinsIncome || 0);
  return (player.winPoints || 0)
    + remainingRounds * (player.winPointsIncome || 0)
    + projectedCoins * conversionRate;
};

const isEconomicArchetype = (archetype) => archetype === BOT_ARCHETYPES.ECONOMIC_POINTS
  || archetype === BOT_ARCHETYPES.ECONOMIC_COINS;
const isAggressiveArchetype = (archetype) => archetype === BOT_ARCHETYPES.AGGRESSIVE_RUSH
  || archetype === BOT_ARCHETYPES.AGGRESSIVE_BUILDUP;

// Сумма своих отрядов в W35_DOCTRINE_LOCAL_FORCE_DISTANCE от кварталов города (порядок двух ходов).
// Держатели своих городов (excludedUnitIds) в штурм не пойдут — их сила не в счет
const calcLocalForce = (playerId, units, quarters, excludedUnitIds) => units.reduce((sum, unit) => (
  unit.ownerId === playerId
  && !excludedUnitIds.has(unit.id)
  && quarters.some((quarterHexKey) => getHexDistance(unit.hexKey, quarterHexKey) <= W35_DOCTRINE_LOCAL_FORCE_DISTANCE)
    ? sum + unit.size
    : sum
), 0);

/**
 * Волна 35 (H): переход архетипа экономической и смешанной доктрины (агрессивные —
 * прежним правилом). Замер по реплеям прода: с 4 раунда у всех фракций
 * ECONOMIC_* и AGGRESSIVE_BUILDUP почти исчезали — любой «слабый город соседа»
 * (почти все города пусты) уводил в AGGRESSIVE_RUSH, и экономические фракции
 * играли как агрессивные (Просветлённые и Вентори в RUSH 53–57% раундов).
 *  - «прикрытый тыл»: у доктрины нет открытого города, требующего гарнизона, без
 *    держателя (selectGarrisonAssignments: required && unitId === null);
 *  - экономическая: в RUSH — только с прикрытым тылом и локальным перевесом у цели
 *    (силы в двух ходах от ее кварталов не меньше двойного гарнизона; держатели
 *    своих городов — гарнизоны и отряды на кварталах обязательных городов — в
 *    перевес не входят: они тыл и прикрывают), в AGGRESSIVE_BUILDUP с 9 раунда —
 *    только с прикрытым тылом;
 *  - смешанная — мягче: только условие прикрытого тыла;
 *  - штурм, не совпадающий со стартовым архетипом, при голом тыле снимается —
 *    возврат к стартовому (иначе раз вошедший в RUSH бот оставался в нем навсегда).
 */
const evaluateDoctrineTransition = ({
  state, player, strategy, units, round, roundLimit, ownArmy, weakCities, isEconomicDoctrine,
}) => {
  // Назначение гарнизонов — лениво и один раз: после дешевых проверок армии и цели.
  // Открытость тыла — своя (W35_DOCTRINE_REAR_EXPOSURE_MODE), не режим гарнизонов G
  let assignments = null;
  const getAssignments = () => {
    if (assignments === null) {
      assignments = selectGarrisonAssignments(state, player.id, {exposureMode: W35_DOCTRINE_REAR_EXPOSURE_MODE});
    }
    return assignments;
  };
  const isRearCovered = () => !getAssignments().some((entry) => entry.required && !entry.unitId);
  // Держатели своих городов: назначенные гарнизоны и отряды на кварталах обязательных городов
  let cityHolderIds = null;
  const getCityHolderIds = () => {
    if (cityHolderIds === null) {
      cityHolderIds = new Set();
      getAssignments().forEach((entry) => {
        if (!entry.required) {
          return;
        }
        if (entry.unitId) {
          cityHolderIds.add(entry.unitId);
        }
        entry.holderIds.forEach((holderId) => cityHolderIds.add(holderId));
      });
    }
    return cityHolderIds;
  };
  const hasEasyCapture = ownArmy >= 6 && weakCities.some((weakCity) => weakCity.garrison * 2 < ownArmy
    && (!isEconomicDoctrine || calcLocalForce(player.id, units, weakCity.quarters, getCityHolderIds()) >= Math.max(
      W35_DOCTRINE_RUSH_MIN_LOCAL_FORCE, weakCity.garrison * W35_DOCTRINE_RUSH_LOCAL_FORCE_RATIO
    )));
  if (hasEasyCapture && isRearCovered()) {
    return BOT_ARCHETYPES.AGGRESSIVE_RUSH;
  }
  const homeArchetype = strategy.w35HomeArchetype
    || getFactionPreferences(player.factionId, player.id, checkIfW42CelectialsStormPlayer(player, selectGameType(state))).archetypes[0];
  let baseArchetype = strategy.archetype;
  if (isAggressiveArchetype(baseArchetype) && baseArchetype !== homeArchetype && !isRearCovered()) {
    baseArchetype = homeArchetype;
  }
  if (isEconomicArchetype(baseArchetype)
    && round >= Math.min(ECONOMIC_AGGRESSION_ROUND, roundLimit - 3)
    && isRearCovered()
  ) {
    return BOT_ARCHETYPES.AGGRESSIVE_BUILDUP;
  }
  return baseArchetype;
};

/**
 * Определяет/переоценивает архетип бота в начале каждого раунда (см. bot_strategy_archetype.md):
 * - стартовый выбор из предпочтений фракции;
 * - слабый город противника в досягаемости -> AGGRESSIVE_RUSH;
 * - подавляющее военное превосходство противника -> DEFENSIVE;
 * - экономические архетипы в поздней игре -> AGGRESSIVE_BUILDUP.
 */
export const evaluateArchetype = (state, player) => {
  const strategy = getBotStrategy(player.id);
  const round = selectRound(state);

  // Волна 21 (20.1, запечено): крысолюды всегда агрессивный раш, без возможности
  // переключения в оборону/экономику — живой тест 20: общая логика
  // переоценки архетипа (защита лидерства, подавляющее превосходство
  // противника) слишком часто уводила их в глухую оборону
  if (isPlayerFaction(player, FACTIONS.RATMEN)) {
    strategy.archetype = BOT_ARCHETYPES.AGGRESSIVE_RUSH;
    strategy.archetypeRound = round;
    return strategy.archetype;
  }

  if (strategy.archetype && strategy.archetypeRound === round) {
    return strategy.archetype;
  }

  // Первичный выбор — из предпочтений фракции.
  // Люди (универсалы) подстраиваются под окружение: агрессивные соседи →
  // BUILDUP/DEFENSIVE, экономические соседи → RUSH (захватить их города)
  // или ECONOMIC (не отстать по доходу)
  if (!strategy.archetype) {
    if (isPlayerFaction(player, FACTIONS.HUMANS)) {
      const opponents = selectPlayers(state).filter((otherPlayer) => otherPlayer.id !== player.id);
      const hasAggressiveNeighbor = opponents.some((opponent) => {
        const primaryArchetype = getFactionPreferences(opponent.factionId).archetypes[0] || '';
        return primaryArchetype.startsWith('AGGRESSIVE');
      });
      strategy.archetype = hasAggressiveNeighbor
        ? getRandomElement([BOT_ARCHETYPES.AGGRESSIVE_BUILDUP, BOT_ARCHETYPES.DEFENSIVE])
        : getRandomElement([BOT_ARCHETYPES.AGGRESSIVE_RUSH, BOT_ARCHETYPES.ECONOMIC_POINTS, BOT_ARCHETYPES.ECONOMIC_COINS]);
      strategy.archetypeRound = round;
      // Волна 35 (H): стартовый («домашний») архетип — к нему возвращается
      // неагрессивная доктрина, когда штурм оставил тыл без гарнизонов
      strategy.w35HomeArchetype = strategy.archetype;
      return strategy.archetype;
    }

    const preferences = getFactionPreferences(
      player.factionId, player.id, checkIfW42CelectialsStormPlayer(player, selectGameType(state))
    );
    strategy.archetype = getRandomElement(preferences.archetypes);
    strategy.archetypeRound = round;
    strategy.w35HomeArchetype = strategy.archetype;
    return strategy.archetype;
  }

  const units = selectUnits(state);
  const players = selectPlayers(state);
  const citiesCache = selectCitiesCache(state);
  const gridRenderer = getBotGridRenderer(state);

  const ownArmy = calcPlayerArmyStrength(player.id, units);
  const strongestOpponent = calcStrongestOpponentArmy(player.id, players, units);

  let nextArchetype = strategy.archetype;

  // Волна 35 (H, гейт isPreW35DoctrinePlayer): доктрина фракции в выборе архетипа
  // и лимит раундов партии вместо FINAL_ROUND (у миссий и локальных партий он свой).
  // Без лимита (миссия с finalRound: null) — прежние 13 раундов проекции
  const isDoctrineActive = !isPreW35DoctrinePlayer(player.id);
  const roundLimit = isDoctrineActive
    ? (getRoundLimit(selectFinalRound(state)) ?? FINAL_ROUND)
    : FINAL_ROUND;

  // Защита лидерства: захваченные точки и строения уже дают доход, проекция
  // которого уверенно превосходит проекции всех противников — прекращаем пуш
  // и защищаем достигнутое (актуально с середины партии)
  const remainingRounds = Math.max(0, roundLimit - round + 1);
  const bestOpponentProjection = players
    .filter((otherPlayer) => otherPlayer.id !== player.id)
    .reduce((best, otherPlayer) => Math.max(best, projectFinalScore(otherPlayer, remainingRounds)), 0);
  const isLeadDecisive = round >= LEAD_PROTECTION_ROUND
    && projectFinalScore(player, remainingRounds) > bestOpponentProjection * LEAD_PROTECTION_RATIO;

  // Волна 33 («сильные города»): у бонусного бота проекция счета раздута его
  // же бонусным доходом (тогда +6/+10 монет в раунд, теперь — по числам
  // configs/playerBonus, фиксированная часть и за каждый город; входят в
  // coinsIncome), поэтому
  // «решающее лидерство» у него — артефакт бонуса, а не перевес на поле.
  // Живой тест: с середины партии такие боты уходили в DEFENSIVE и переставали
  // давить соседей. Защита лидерства для них выключена на всю партию;
  // оборона при подавляющем превосходстве противника остается
  const isLeadProtectionActive = !getBotBonusProfile(player, selectGameType(state))?.strongCities;

  // Волна 20 (19.5): зверолюды почти никогда не переходят в защитный/
  // экономический архетип — до сих пор слишком пассивны на живых тестах.
  // Переход в оборону остается только при КАТАСТРОФИЧЕСКОМ превосходстве
  // противника (порог вдвое выше обычного), проекция лидерства их не тормозит
  const isBeastmenPlayer = isPlayerFaction(player, FACTIONS.BEASTMEN);
  const beastmenOverwhelmedRatio = OVERWHELMED_RATIO * 2;

  // Подавляющее превосходство противника — уходим в оборону
  if (isBeastmenPlayer
    ? strongestOpponent >= Math.max(10, ownArmy * beastmenOverwhelmedRatio)
    : strongestOpponent >= Math.max(6, ownArmy * OVERWHELMED_RATIO)
  ) {
    nextArchetype = BOT_ARCHETYPES.DEFENSIVE;
  } else if (isLeadDecisive && !isBeastmenPlayer && isLeadProtectionActive) {
    nextArchetype = BOT_ARCHETYPES.DEFENSIVE;
  } else {
    // Слабозащищенный город противника при нашем военном преимуществе — переключаемся на раш
    const weakCities = findWeakEnemyCities(state, player, units, citiesCache, players, gridRenderer, ownArmy);
    const doctrine = getFactionGarrisonDoctrine(
      player.factionId, checkIfW42CelectialsStormPlayer(player, selectGameType(state))
    );

    if (!isDoctrineActive || doctrine === GARRISON_DOCTRINES.AGGRESSIVE) {
      const hasEasyCapture = weakCities.some((weakCity) => weakCity.garrison * 2 < ownArmy);

      if (hasEasyCapture && ownArmy >= 6) {
        nextArchetype = BOT_ARCHETYPES.AGGRESSIVE_RUSH;
      } else if (
        (strategy.archetype === BOT_ARCHETYPES.ECONOMIC_POINTS
          || strategy.archetype === BOT_ARCHETYPES.ECONOMIC_COINS)
        && round >= Math.min(ECONOMIC_AGGRESSION_ROUND, roundLimit - 3)
      ) {
        nextArchetype = BOT_ARCHETYPES.AGGRESSIVE_BUILDUP;
      }
    } else {
      nextArchetype = evaluateDoctrineTransition({
        state, player, strategy, units, round, roundLimit, ownArmy, weakCities,
        isEconomicDoctrine: doctrine === GARRISON_DOCTRINES.ECONOMIC,
      });
    }
  }

  strategy.archetype = nextArchetype;
  strategy.archetypeRound = round;
  return strategy.archetype;
};
